import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles } from "@material-ui/core";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Autocomplete, {
	createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  maxWidth: {
    width: "100%",
  },
	actionButton: {
		borderRadius: 999,
		boxShadow: "none",
		textTransform: "none",
		"&:hover": {
			boxShadow: "none",
		},
	},
}));

const filterOptions = createFilterOptions({
	trim: true,
});

const TransferTicketModal = ({
	modalOpen,
	onClose,
	ticketid,
	ticket,
	pendingAction = "open",
	autoAssignLoggedUserAndQueue = false,
}) => {
	const history = useHistory();
	const { user } = useContext(AuthContext);
	const [options, setOptions] = useState([]);
	const [allQueues, setAllQueues] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedQueue, setSelectedQueue] = useState('');
	const classes = useStyles();
	const { findAll: findAllQueues } = useQueues();
	const isPendingTicket = ticket?.status === "pending";
	const userQueues = Array.isArray(user?.queues) ? user.queues : [];

	useEffect(() => {
		if (!modalOpen) {
			return;
		}

		if (
			autoAssignLoggedUserAndQueue &&
			isPendingTicket &&
			pendingAction === "open"
		) {
			const ticketQueueId = Number(ticket?.queueId);
			const userBelongsToTicketQueue = userQueues.some(
				queue => Number(queue.id) === ticketQueueId
			);

			const autoSelectedQueueId = userBelongsToTicketQueue
				? String(ticketQueueId)
				: userQueues[0]
					? String(userQueues[0].id)
					: "";

			setSelectedQueue(autoSelectedQueueId);
			setSelectedUser(
				user?.id
					? {
						id: user.id,
						name: user.name,
						queues: userQueues,
					  }
					: null
			);
			return;
		}

		setSelectedQueue(ticket?.queueId ? String(ticket.queueId) : "");
		setSelectedUser(ticket?.user ? ticket.user : null);
	}, [
		modalOpen,
		ticket,
		autoAssignLoggedUserAndQueue,
		isPendingTicket,
		pendingAction,
		user,
		userQueues,
	]);

	useEffect(() => {
		const loadQueues = async () => {
			const list = await findAllQueues();
			setAllQueues(list);
		}
		loadQueues();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!modalOpen) {
			setLoading(false);
			return;
		}

		if (!selectedQueue) {
			setOptions([]);
			setLoading(false);
			return;
		}

		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchUsers = async () => {
				try {
					const { data } = await api.get("/users/transfer-options", {
						params: {
							searchParam,
							...(selectedQueue ? { queueId: selectedQueue } : {}),
						},
					});
					setOptions(data.users);
					setLoading(false);
				} catch (err) {
					setLoading(false);
					toastError(err);
				}
			};

			fetchUsers();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, modalOpen, selectedQueue]);

	useEffect(() => {
		if (!selectedUser?.id || options.length === 0) {
			return;
		}

		const freshSelectedUser = options.find(
			option => Number(option.id) === Number(selectedUser.id)
		);

		if (
			freshSelectedUser &&
			freshSelectedUser.isOnline !== selectedUser.isOnline
		) {
			setSelectedUser(freshSelectedUser);
		}
	}, [options, selectedUser]);

	const handleClose = () => {
		onClose();
		setSearchParam("");
		setSelectedUser(null);
		setSelectedQueue("");
		setOptions([]);
	};

	const handleSaveTicket = async e => {
		e.preventDefault();
		if (!ticketid) return;
		if (isPendingTicket && !selectedQueue) {
			return;
		}

		setLoading(true);
		try {
			const data = {};
			const targetQueue = allQueues.find(
				queue => Number(queue.id) === Number(selectedQueue)
			);
			const targetQueueAllowsManual = targetQueue?.allowManualAttendance !== false;

			if (isPendingTicket) {
				data.queueId = Number(selectedQueue);

				if (selectedUser) {
					data.status = pendingAction;
					data.userId = selectedUser.id;
				} else {
					data.status = "pending";
					data.userId = null;
				}
			} else if (selectedQueue && selectedQueue !== null) {
				data.queueId = selectedQueue;

				if (!selectedUser) {
					data.status = "pending";
					data.userId = null;
				} else {
					data.status = "open";
					data.userId = selectedUser.id;
				}
			} else {
				data.userId = selectedUser ? selectedUser.id : null;
			}

			await api.put(`/tickets/${ticketid}`, data);

			setLoading(false);
			handleClose();

			if (Number(data.userId) === Number(user?.id) && data.status === "open") {
				history.push(`/tickets/${ticketid}`, { switchTab: "open" });
			} else {
				history.push(`/tickets`, {
					switchTab:
						data.status === "closed" && isPendingTicket
							? "pending"
							: data.status === "pending"
								? "pending"
								: "open",
				});
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	const selectedUserIsOnline =
		options.find(option => Number(option.id) === Number(selectedUser?.id))
			?.isOnline ?? selectedUser?.isOnline;

	return (
		<Dialog open={modalOpen} onClose={handleClose} maxWidth="lg" scroll="paper">
			<form onSubmit={handleSaveTicket}>
				<DialogTitle id="form-dialog-title">
					{i18n.t("transferTicketModal.title")}
				</DialogTitle>
				<DialogContent dividers>
					<FormControl variant="outlined" className={classes.maxWidth} style={{ marginBottom: 20 }}>
						<InputLabel>{i18n.t("transferTicketModal.fieldQueueLabel")}</InputLabel>
						<Select
							value={selectedQueue}
							onChange={(e) => {
								const newQueueId = e.target.value;
								setSelectedQueue(newQueueId);
								setSearchParam("");
								setOptions([]);

								if (
									selectedUser &&
									newQueueId &&
									Array.isArray(selectedUser.queues) &&
									!selectedUser.queues.some(queue => queue.id === newQueueId)
								) {
									setSelectedUser(null);
								}

								if (!newQueueId) {
									setSelectedUser(null);
								}
							}}
							label={i18n.t("transferTicketModal.fieldQueuePlaceholder")}
						>
							<MenuItem value={''}>&nbsp;</MenuItem>
							{allQueues.map((queue) => (
								<MenuItem key={queue.id} value={queue.id}>{queue.name}</MenuItem>
							))}
						</Select>
					</FormControl>
					<Autocomplete
						style={{ width: 300, marginBottom: 20 }}
						value={selectedUser}
						getOptionLabel={option => `${option.name}`}
						onChange={(e, newValue) => {
							setSelectedUser(newValue);
						}}
						options={options}
						filterOptions={filterOptions}
						autoHighlight
						disabled={!selectedQueue}
						noOptionsText={i18n.t("transferTicketModal.noQueueUsers")}
						loading={loading}
						renderOption={option => (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									width: "100%",
									gap: 8,
								}}
							>
								<span>{option.name}</span>
								<span
									style={{
										width: 16,
										height: 16,
										borderRadius: "50%",
										backgroundColor: option?.isOnline ? "#2e7d32" : "#d32f2f",
										display: "inline-block",
										flexShrink: 0,
									}}
								/>
							</div>
						)}
						renderInput={params => (
							<TextField
								{...params}
								label={i18n.t("transferTicketModal.fieldLabel")}
								variant="outlined"
								autoFocus={Boolean(selectedQueue)}
								placeholder={
									selectedQueue
										? undefined
										: i18n.t("transferTicketModal.fieldQueueLabel")
								}
								onChange={e => setSearchParam(e.target.value)}
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<React.Fragment>
											{selectedUser ? (
												<span
													style={{
														width: 16,
														height: 16,
														borderRadius: "50%",
														backgroundColor: selectedUserIsOnline ? "#2e7d32" : "#d32f2f",
														display: "inline-block",
														marginRight: 8,
														flexShrink: 0,
													}}
												/>
											) : null}
											{loading ? (
												<CircularProgress color="inherit" size={20} />
											) : null}
											{params.InputProps.endAdornment}
										</React.Fragment>
									),
								}}
							/>
						)}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleClose}
						color="secondary"
						disabled={loading}
						variant="outlined"
						className={classes.actionButton}
					>
						{i18n.t("transferTicketModal.buttons.cancel")}
					</Button>
					<ButtonWithSpinner
						variant="contained"
						type="submit"
						color="primary"
						className={classes.actionButton}
						disabled={isPendingTicket && !selectedQueue}
						loading={loading}
					>
						{i18n.t("transferTicketModal.buttons.ok")}
					</ButtonWithSpinner>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default TransferTicketModal;
